import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Order,
  Model,
  Workshop,
  Technic,
  Firm,
  OrderUnit,
  User,
} from "../types";
import {
  orderService,
  exchangeRateService,
  userService,
} from "../services/dataService";
import ModelModal from "./ModelModal";
import WorkshopModal from "./WorkshopModal";
import TechnicModal from "./TechnicModal";
import FirmPickerField from "./FirmPicker";
import AlertModal from "./AlertModal";
import "../pages/PurchaseOrders.css";
import "./OrderEditModal.css";

interface OrderEditModalProps {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
  onSave: (updatedOrder: Order) => void;
}

interface Currency {
  code: string;
  name: string;
}

const UNIT_OPTIONS: { value: OrderUnit; label: string }[] = [
  { value: OrderUnit.Adet, label: "Adet" },
  { value: OrderUnit.Metre, label: "Metre" },
  { value: OrderUnit.Takim, label: "Takım" },
  { value: OrderUnit.Pano, label: "Pano" },
];
const PRIORITY_OPTIONS = [
  { value: "", label: "Yok" },
  { value: "Düşük", label: "Düşük" },
  { value: "Normal", label: "Normal" },
  { value: "Yüksek", label: "Yüksek" },
  { value: "Acil", label: "Acil" },
];
const NOTE_MAX = 500;

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M7 7l10 10M17 7 7 17" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  model: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3 4 6l2 4 2-1v12h8V9l2 1 2-4-4-3a4 4 0 0 1-8 0z" /></svg>,
  ws: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V9l6 3V9l6 3V9l6 3v9z" /></svg>,
};

/**
 * Sipariş düzenleme penceresi (Siparişler, Sipariş Detayı, Atölye Kanban). Sipariş formunun (/orders/edit)
 * po-form-sec düzenini izler; alan kümesi ve PUT gövdesi eskisiyle aynıdır (resimler/en-boy burada düzenlenmez,
 * olduğu gibi taşınır).
 */
const OrderEditModal: React.FC<OrderEditModalProps> = ({
  isOpen,
  orderId,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [tried, setTried] = useState(false);
  const initialDataRef = useRef<string>("");
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const focusedRef = useRef(false);

  // Form state
  const [formData, setFormData] = useState({
    deadline: "",
    quantity: "1" as string,
    unit: OrderUnit.Adet,
    pieceCount: 1,
    price: "",
    currency: "TRY",
    priority: "",
    note: "",
  });

  // Selection state
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState<Workshop | null>(null);
  const [selectedFirm, setSelectedFirm] = useState<Firm | null>(null);
  const [selectedTechnics, setSelectedTechnics] = useState<Technic[]>([]);
  const [selectedModelist, setSelectedModelist] = useState<User | null>(null);

  // Data lists
  const [modelists, setModelists] = useState<User[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  // Modal states
  const [showModelModal, setShowModelModal] = useState(false);
  const [showWorkshopModal, setShowWorkshopModal] = useState(false);
  const [showTechnicModal, setShowTechnicModal] = useState(false);

  // Alert Modal
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Track changes
  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      formData,
      firmId: selectedFirm?.firmId,
      modelId: selectedModel?.modelId,
      workshopId: selectedWorkshop?.workshopId,
      modelistId: selectedModelist?.userId,
      technicIds: selectedTechnics.map((t) => t.technicId).sort(),
    });
  }, [formData, selectedFirm, selectedModel, selectedWorkshop, selectedModelist, selectedTechnics]);

  useEffect(() => {
    if (!loading && initialDataRef.current) {
      setHasChanges(getFormSnapshot() !== initialDataRef.current);
    }
  }, [getFormSnapshot, loading]);

  // Esc: önce iç katman (firma seçici, uyarı, model/atölye/teknik penceresi), sonra bu pencere. Kayıt sürerken kapanmaz.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector(".fp-overlay")) return; // firma seçici Esc'i kendisi tüketir
      e.stopPropagation();
      if (alertModal.isOpen) setAlertModal((a) => ({ ...a, isOpen: false }));
      else if (showTechnicModal) setShowTechnicModal(false);
      else if (showWorkshopModal) setShowWorkshopModal(false);
      else if (showModelModal) setShowModelModal(false);
      else if (!saving) onClose();
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, onClose, saving, alertModal.isOpen, showTechnicModal, showWorkshopModal, showModelModal]);

  // Kapanınca odak açan öğeye döner
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    return () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && orderId) {
      // Bileşen bazı sayfalarda hep bağlı: önceki siparişin anlık görüntüsü/seçimleri taşınmasın
      initialDataRef.current = "";
      focusedRef.current = false;
      setHasChanges(false);
      setTried(false);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Paralel yükleme
      const [orderData, modelistsData, currenciesData] = await Promise.all([
        orderService.getById(orderId),
        userService.getModelists(),
        exchangeRateService.getCurrencies(),
      ]);

      if (!orderData) {
        setOrder(null);
        setAlertModal({ isOpen: true, message: "Sipariş bulunamadı!", type: "error" });
        return;
      }

      setOrder(orderData);
      setModelists(modelistsData);
      setCurrencies(currenciesData);

      // Form verilerini doldur
      setFormData({
        deadline: orderData.deadline ? orderData.deadline.split("T")[0] : "",
        quantity: orderData.quantity ? String(orderData.quantity).replace(".", ",") : "1",
        unit: orderData.unit !== undefined ? orderData.unit : OrderUnit.Adet,
        pieceCount: orderData.pieceCount || 1,
        price: orderData.price?.toString() || "",
        currency: orderData.priceCurrency || orderData.currency || "TRY",
        priority: orderData.priority || "",
        note: orderData.note || "",
      });

      // Seçimleri doldur
      setSelectedFirm(orderData.firm || null);
      setSelectedModel(orderData.model || null);
      setSelectedWorkshop(orderData.workshop || null);

      // Modelist (yoksa önceki siparişten kalan seçim temizlenir)
      setSelectedModelist(
        orderData.modelistUserId ? modelistsData.find((m) => m.userId === orderData.modelistUserId) || null : null,
      );

      // Teknikler (tekilleştirilmiş; yoksa boş)
      const technics = (orderData.orderTechnics ?? [])
        .map((ot) => ot.technic)
        .filter((t): t is Technic => t !== undefined);
      setSelectedTechnics(
        technics.filter((technic, index, self) => index === self.findIndex((t) => t.technicId === technic.technicId)),
      );
    } catch (error) {
      console.error("❌ Failed to load order:", error);
      setOrder(null);
      setAlertModal({ isOpen: true, message: "Sipariş yüklenemedi!", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Set initial snapshot after loading completes
  useEffect(() => {
    if (!loading && order) {
      const snap = getFormSnapshot();
      if (!initialDataRef.current) {
        initialDataRef.current = snap;
      }
    }
  }, [loading, order, getFormSnapshot]);

  // Yükleme bitince ilk alana odak (açılış başına bir kez)
  useEffect(() => {
    if (!isOpen || loading || !order || focusedRef.current) return;
    focusedRef.current = true;
    bodyRef.current?.querySelector<HTMLElement>("button, input, select, textarea")?.focus({ preventScroll: true });
  }, [isOpen, loading, order]);

  const handleSubmit = async (e: React.FormEvent) => {
    // Firma seçicinin "yeni firma" formu portal olsa da React ağacında bu formun içinde; onun submit'i burayı tetiklemesin
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    setTried(true);

    if (!selectedFirm || !selectedModel) return;
    if (!order || saving) return;

    try {
      setSaving(true);

      const orderTechnics = selectedTechnics.map((technic) => ({
        orderTechnicId: undefined,
        orderId: order.orderId,
        technicId: technic.technicId,
        technic: undefined,
      }));

      const updateData = {
        orderId: order.orderId,
        acceptanceDate: order.acceptanceDate,
        completionDate: order.completionDate,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : undefined,
        firmId: selectedFirm.firmId,
        modelId: selectedModel.modelId,
        quantity: parseFloat(formData.quantity.replace(",", ".")) || 1,
        // En/Boy bu modalda düzenlenmiyor ama PUT körlemesine kopyalıyor —
        // taşınmazsa her düzenlemede siliniyorlar.
        width: order.width,
        height: order.height,
        orderUnitId: formData.unit,
        pieceCount: formData.unit === OrderUnit.Takim ? formData.pieceCount : undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        priceCurrency: formData.currency,
        vatRate: order.vatRate,
        workshopId: selectedWorkshop?.workshopId || undefined,
        operatorId: order.operatorId,
        modelistUserId: selectedModelist?.userId || undefined,
        priority: formData.priority || undefined,
        note: formData.note || undefined,
        invoice: order.invoice,
        invoiceNumber: order.invoiceNumber,
        status: order.status,
        orderStatusId: order.status,
        qrCodeUrl: order.qrCodeUrl,
        createdAt: order.createdAt,
        createdBy: order.createdBy,
        isActive: order.isActive,
        orderTechnics: orderTechnics,
        images: order.images || [],
      };

      await orderService.update(order.orderId, updateData as unknown as Order);

      // Güncellenmiş veriyi parent'a bildir
      const updatedOrder: Order = {
        ...order,
        ...updateData,
        firm: selectedFirm,
        model: selectedModel,
        workshop: selectedWorkshop || undefined,
      };

      onSave(updatedOrder);

      setAlertModal({ isOpen: true, message: "Sipariş başarıyla güncellendi!", type: "success" });

      // Kısa bir delay sonra kapat
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      console.error("❌ Failed to update order:", error);
      setAlertModal({
        isOpen: true,
        message: error.response?.data?.message || "Sipariş güncellenemedi!",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSelectTechnic = (technic: Technic) => {
    setSelectedTechnics((prev) => {
      const isAlreadySelected = prev.some((t) => t.technicId === technic.technicId);
      if (isAlreadySelected) {
        return prev.filter((t) => t.technicId !== technic.technicId);
      }
      return [...prev, technic];
    });
  };

  if (!isOpen) return null;

  // Hep bağlı kalan sayfalarda yeniden açılışın ilk karesinde önceki sipariş görünmesin
  const busy = loading || (!!order && order.orderId !== orderId);
  const shown = busy ? null : order;
  const qty = parseFloat(formData.quantity.replace(",", ".")) || 1;
  const totalPrice = formData.price ? parseFloat(formData.price) * qty : 0;
  const isTakim = formData.unit === OrderUnit.Takim;
  // Eski kayıtlarda listede olmayan bir öncelik (ör. "Orta") kaybolmasın
  const priorityOptions = formData.priority && !PRIORITY_OPTIONS.some((o) => o.value === formData.priority)
    ? [...PRIORITY_OPTIONS, { value: formData.priority, label: formData.priority }]
    : PRIORITY_OPTIONS;
  const currencyOptions = currencies.some((c) => c.code === formData.currency) || !formData.currency
    ? currencies
    : [{ code: formData.currency, name: "" }, ...currencies];

  return (
    // "center" (top değil): firma seçici (.fp-overlay, z 1200) bu pencerenin üstünde açılmalı
    <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="po-modal wide oem" role="dialog" aria-modal="true" aria-labelledby="oem-title" onSubmit={handleSubmit} noValidate>
        <div className="po-modal-head">
          <div className="oem-headtext">
            <span className="po-kicker">Siparişi düzenle</span>
            <h3 id="oem-title">
              {shown ? shown.model?.modelCode || shown.model?.modelName || "Sipariş" : "Sipariş düzenle"}
              {shown && <span className="po-codepill">#{shown.orderId.substring(0, 8)}</span>}
            </h3>
            {shown?.firm?.firmName && <p className="oem-sub">{shown.firm.firmName}</p>}
          </div>
          <div className="po-modal-head-side">
            {hasChanges && !saving && !busy && <span className="po-dirty">Kaydedilmemiş değişiklik</span>}
            <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{I.close}</button>
          </div>
        </div>

        <div className="po-modal-body" ref={bodyRef}>
          {busy ? (
            <div className="po-loading"><span className="po-spin" />Sipariş yükleniyor…</div>
          ) : !order ? (
            <div className="po-blank"><b>Sipariş yüklenemedi</b>Pencereyi kapatıp yeniden deneyin.</div>
          ) : (
            <>
              {/* Firma ve model */}
              <div className="po-form-sec">
                <h4>Firma ve model</h4>
                <div className="po-grid2">
                  <div className="po-field">
                    <span>Firma <em>zorunlu</em></span>
                    <FirmPickerField
                      value={selectedFirm?.firmId || null}
                      firm={selectedFirm}
                      onChange={(f) => setSelectedFirm(f)}
                      placeholder="Firma seçin"
                      className={tried && !selectedFirm ? "po-invalid" : ""}
                    />
                    {tried && !selectedFirm && <span className="po-fielderr">Firma seçin.</span>}
                  </div>
                  <div className="po-field">
                    <span>Model <em>zorunlu</em></span>
                    <button
                      type="button"
                      className={`oem-pick ${selectedModel ? "has" : ""} ${tried && !selectedModel ? "invalid" : ""}`}
                      onClick={() => setShowModelModal(true)}
                      aria-haspopup="dialog"
                      title={selectedModel?.modelName}
                    >
                      <span className="oem-pick-mark">{I.model}</span>
                      <span className="oem-pick-text">
                        {selectedModel ? (
                          <>
                            <b>{selectedModel.modelCode || selectedModel.modelName}</b>
                            {selectedModel.modelCode && selectedModel.modelName && selectedModel.modelName !== selectedModel.modelCode && <small>{selectedModel.modelName}</small>}
                          </>
                        ) : <b className="ph">Model seçmek için tıklayın</b>}
                      </span>
                      <span className="po-btn small oem-pick-btn">{selectedModel ? "Değiştir" : "Seç"}</span>
                    </button>
                    {tried && !selectedModel && <span className="po-fielderr">Model seçin.</span>}
                  </div>
                </div>
              </div>

              {/* Miktar ve fiyat */}
              <div className="po-form-sec">
                <h4>Miktar ve fiyat</h4>
                <div className="po-grid3">
                  <label className="po-field">
                    <span>Miktar</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.quantity}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^[0-9]*[,.]?[0-9]*$/.test(val)) {
                          setFormData({ ...formData, quantity: val });
                        }
                      }}
                    />
                  </label>
                  <div className={`po-field ${isTakim ? "" : "span2"}`}>
                    <span>Birim</span>
                    <div className="po-seg oem-seg fill" role="radiogroup" aria-label="Birim">
                      {UNIT_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          role="radio"
                          aria-checked={formData.unit === o.value}
                          className={formData.unit === o.value ? "on" : ""}
                          onClick={() => setFormData({ ...formData, unit: o.value })}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {isTakim && (
                    <label className="po-field">
                      <span>Takımda parça sayısı</span>
                      <input
                        type="number"
                        min="1"
                        inputMode="numeric"
                        value={formData.pieceCount}
                        onChange={(e) => setFormData({ ...formData, pieceCount: parseInt(e.target.value) || 1 })}
                      />
                    </label>
                  )}
                  <label className="po-field">
                    <span>Birim fiyat</span>
                    <div className="po-money">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="0,00"
                      />
                      <i>{formData.currency}</i>
                    </div>
                  </label>
                  <label className="po-field">
                    <span>Para birimi</span>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    >
                      {currencyOptions.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name ? `${c.code} - ${c.name}` : c.code}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {/* Atölye ve desinatör */}
              <div className="po-form-sec">
                <h4>Atölye ve desinatör</h4>
                <div className="po-grid2">
                  <div className="po-field">
                    <span>
                      Atölye <em>isteğe bağlı</em>
                      {selectedWorkshop && (
                        <button type="button" className="po-link oem-clear" onClick={() => setSelectedWorkshop(null)}>Kaldır</button>
                      )}
                    </span>
                    <button
                      type="button"
                      className={`oem-pick ${selectedWorkshop ? "has" : ""}`}
                      onClick={() => setShowWorkshopModal(true)}
                      aria-haspopup="dialog"
                    >
                      <span className="oem-pick-mark">{I.ws}</span>
                      <span className="oem-pick-text">
                        {selectedWorkshop ? <b>{selectedWorkshop.name}</b> : <b className="ph">Atölye seçin</b>}
                      </span>
                      <span className="po-btn small oem-pick-btn">{selectedWorkshop ? "Değiştir" : "Seç"}</span>
                    </button>
                  </div>
                  <label className="po-field">
                    <span>Desinatör <em>isteğe bağlı</em></span>
                    <select
                      value={selectedModelist?.userId || ""}
                      onChange={(e) => {
                        const modelist = modelists.find((m) => m.userId === e.target.value);
                        setSelectedModelist(modelist || null);
                      }}
                    >
                      <option value="">Seçilmedi</option>
                      {modelists.map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.firstName} {m.lastName}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {/* Teknikler */}
              <div className="po-form-sec">
                <h4>
                  Teknikler
                  {selectedTechnics.length > 0 && <em>{selectedTechnics.length}</em>}
                </h4>
                {selectedTechnics.length > 0 ? (
                  <div className="oem-chips">
                    {selectedTechnics.map((t) => (
                      <span key={t.technicId} className="oem-chip">
                        {t.name}
                        <button type="button" aria-label={`${t.name} tekniğini kaldır`} title="Kaldır" onClick={() => handleSelectTechnic(t)}>
                          {I.x}
                        </button>
                      </span>
                    ))}
                    <button type="button" className="po-btn small oem-chip-add" onClick={() => setShowTechnicModal(true)}>
                      {I.plus}Ekle / çıkar
                    </button>
                  </div>
                ) : (
                  <button type="button" className="oem-empty" onClick={() => setShowTechnicModal(true)}>
                    <b>Henüz teknik seçilmedi</b>
                    <span>Baskı, nakış vb. teknikleri seçmek için tıklayın.</span>
                  </button>
                )}
              </div>

              {/* Termin, öncelik ve not */}
              <div className="po-form-sec">
                <h4>Termin, öncelik ve not</h4>
                <div className="oem-row">
                  <label className="po-field oem-date">
                    <span>Termin tarihi</span>
                    <input
                      type="date"
                      value={formData.deadline}
                      onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    />
                  </label>
                  <div className="po-field">
                    <span>Öncelik</span>
                    <div className="po-seg oem-seg" role="radiogroup" aria-label="Öncelik">
                      {priorityOptions.map((o) => (
                        <button
                          key={o.value || "none"}
                          type="button"
                          role="radio"
                          aria-checked={formData.priority === o.value}
                          className={`${formData.priority === o.value ? "on" : ""} ${o.value === "Acil" ? "urgent" : ""}`}
                          onClick={() => setFormData({ ...formData, priority: o.value })}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <label className="po-field">
                  <span>Not</span>
                  <textarea
                    value={formData.note}
                    maxLength={NOTE_MAX}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    placeholder="Sipariş notu ekleyin..."
                    rows={3}
                  />
                </label>
                <span className="po-counter">{formData.note.length}/{NOTE_MAX}</span>
              </div>
            </>
          )}
        </div>

        <div className="po-modal-foot between">
          <div className="po-sum">
            <div className="grand">
              <span>Toplam</span>
              <b>
                {totalPrice > 0
                  ? `${totalPrice.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${formData.currency}`
                  : "—"}
              </b>
            </div>
          </div>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button type="submit" className="po-btn primary" disabled={busy || !order || saving || !hasChanges}>
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      </form>

      {/* Alt pencereler (form dışında: kendi formlarının submit'i bu formu tetiklemesin) */}
      <ModelModal
        isOpen={showModelModal}
        onClose={() => setShowModelModal(false)}
        onSelect={(model: Model) => {
          setSelectedModel(model);
          setShowModelModal(false);
        }}
      />

      <WorkshopModal
        isOpen={showWorkshopModal}
        onClose={() => setShowWorkshopModal(false)}
        onSelect={(workshop: Workshop) => {
          setSelectedWorkshop(workshop);
          setShowWorkshopModal(false);
        }}
      />

      <TechnicModal
        isOpen={showTechnicModal}
        onClose={() => setShowTechnicModal(false)}
        onSelectTechnic={handleSelectTechnic}
        selectedTechnics={selectedTechnics}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
};

export default OrderEditModal;
